import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { ApiError, api } from '@/api/client'
import type { BookingResult } from '@/api/types'
import * as d from '@/lib/dates'
import { STATUS_LABELS, guests, money, nights } from '@/lib/format'

export function BookingPage() {
  const { slug = '' } = useParams()
  const [params] = useSearchParams()

  const checkIn = params.get('check_in') ?? ''
  const checkOut = params.get('check_out') ?? ''
  const adults = Number(params.get('adults') ?? 2)
  const children = Number(params.get('children') ?? 0)
  const roomTypeId = params.get('room_type') ?? ''
  const ratePlanId = params.get('rate_plan') ?? ''

  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', comment: '' })
  const [done, setDone] = useState<BookingResult | null>(null)

  // Ключ идемпотентности живёт весь визит: повторная отправка формы или
  // случайный второй клик не создадут вторую бронь.
  const idempotencyKey = useMemo(
    () => `${slug}-${checkIn}-${checkOut}-${roomTypeId}-${crypto.randomUUID()}`,
    [slug, checkIn, checkOut, roomTypeId],
  )

  const offers = useQuery({
    queryKey: ['offers', slug, checkIn, checkOut, adults, children],
    queryFn: () =>
      api.offers(slug, { check_in: checkIn, check_out: checkOut, adults, children }),
    enabled: Boolean(checkIn && checkOut),
  })

  const offer = offers.data?.offers.find(
    (o) => o.room_type_id === roomTypeId && o.rate_plan_id === ratePlanId,
  )

  const submit = useMutation({
    mutationFn: () =>
      api.book(
        slug,
        {
          check_in: checkIn,
          check_out: checkOut,
          guest: {
            first_name: form.first_name.trim(),
            last_name: form.last_name.trim() || null,
            email: form.email.trim(),
            phone: form.phone.trim() || null,
          },
          items: [{ room_type_id: roomTypeId, rate_plan_id: ratePlanId, adults, children }],
          comment: form.comment.trim() || null,
        },
        idempotencyKey,
      ),
    onSuccess: setDone,
  })

  if (done) return <Confirmation result={done} email={form.email} />

  const nightCount = checkIn && checkOut ? d.nightsBetween(checkIn, checkOut) : 0
  const ready = form.first_name.trim() && form.email.trim().includes('@')

  return (
    <section className="section booking">
      <Link className="back" to={`/hotel/${slug}`}>
        ← К объекту
      </Link>
      <h1 className="display booking__title">Бронирование</h1>

      {offers.isLoading && <p className="muted">Проверяем, свободен ли номер…</p>}

      {offers.data && !offer && (
        <div className="empty">
          <p className="empty__title">Этот номер уже заняли</p>
          <p className="muted">Пока вы выбирали, его забронировали. Посмотрите другие варианты.</p>
          <Link className="button" to={`/hotel/${slug}`}>
            Вернуться к выбору
          </Link>
        </div>
      )}

      {offer && (
        <div className="booking__grid">
          <form
            className="booking__form"
            onSubmit={(e) => {
              e.preventDefault()
              if (ready) submit.mutate()
            }}
          >
            <label className="field">
              <span className="field__label">Имя *</span>
              <input
                className="field__input"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                required
              />
            </label>

            <label className="field">
              <span className="field__label">Фамилия</span>
              <input
                className="field__input"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              />
            </label>

            <label className="field">
              <span className="field__label">Email *</span>
              <input
                className="field__input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
              <span className="field__hint">На него придёт номер брони</span>
            </label>

            <label className="field">
              <span className="field__label">Телефон</span>
              <input
                className="field__input"
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </label>

            <label className="field">
              <span className="field__label">Пожелания</span>
              <textarea
                className="field__input field__input--area"
                rows={3}
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                placeholder="Поздний заезд, детская кроватка"
              />
            </label>

            {submit.isError && (
              <p className="error">
                {submit.error instanceof ApiError
                  ? submit.error.message
                  : 'Не удалось отправить заявку'}
              </p>
            )}

            <button
              className="button button--primary button--wide"
              type="submit"
              disabled={!ready || submit.isPending}
            >
              {submit.isPending ? 'Отправляем…' : 'Отправить заявку'}
            </button>

            <p className="field__hint">
              Оплата не требуется. Хозяин подтвердит бронь и свяжется с вами.
            </p>
          </form>

          <aside className="summary">
            <h2 className="summary__title">{offer.room_type_name}</h2>
            <dl className="summary__list">
              <div>
                <dt>Заезд</dt>
                <dd>{d.formatLong(checkIn)}</dd>
              </div>
              <div>
                <dt>Выезд</dt>
                <dd>{d.formatLong(checkOut)}</dd>
              </div>
              <div>
                <dt>Гостей</dt>
                <dd>{guests(adults + children)}</dd>
              </div>
              <div>
                <dt>Ночей</dt>
                <dd>{nights(nightCount)}</dd>
              </div>
            </dl>
            <div className="summary__total">
              <span>Итого</span>
              <strong>{money(offer.total_price, offers.data?.currency)}</strong>
            </div>
          </aside>
        </div>
      )}
    </section>
  )
}

function Confirmation({ result, email }: { result: BookingResult; email: string }) {
  return (
    <section className="section done">
      <h1 className="display done__title">Заявка принята</h1>
      <p className="done__ref">
        Номер брони <strong>{result.reference}</strong>
      </p>
      <p className="muted">
        {STATUS_LABELS[result.status] ?? result.status}. Мы отправили подтверждение на {email}.
      </p>

      <dl className="summary__list done__list">
        <div>
          <dt>Объект</dt>
          <dd>{result.property_name}</dd>
        </div>
        <div>
          <dt>Даты</dt>
          <dd>
            {d.formatLong(result.check_in)} — {d.formatLong(result.check_out)}
          </dd>
        </div>
        <div>
          <dt>Сумма</dt>
          <dd>{money(result.total_amount, result.currency)}</dd>
        </div>
      </dl>

      <div className="done__actions">
        <Link className="button" to="/">
          На главную
        </Link>
        <Link
          className="button button--primary"
          to={`/booking?reference=${result.reference}&email=${encodeURIComponent(email)}`}
        >
          Статус брони
        </Link>
      </div>
    </section>
  )
}
